import React, { Fragment, ReactElement, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import API from '@sesame/web-api';
import { Spinner } from 'src/components/Spinner';
import { ILoadState, IRack, IRackComponent, IComponent, IComponentColumn, ISalesorder } from 'src/types';
import Alert, { AlertTypes } from 'src/components/AlertDisplay';
import { makeSortedComponents, sortTree } from './rackUtils';
import { EditComponentPortmap } from './EditComponentPortmap';
import { beep } from 'src/util/PlaySound';


interface IRackSerialComponentsProps {
  rackId: number;
  onElevationChange: () => void;
}
export const RackSerialComponents = ({ rackId, onElevationChange }: IRackSerialComponentsProps) => {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [componentTree, setComponentTree] = useState<IRackComponent>();
  const [displayState, setDisplayState] = useState<ILoadState>(ILoadState.NEW);
  const [serial, setSerial] = useState('');
  const [saveSerial, setSaveSerial] = useState(false);
  const [ou, setOu] = useState('');
  const [column, setColumn] = useState('');
  const [validOU, setValidOU] = useState(true);
  const [validColumn, setValidColumn] = useState(true);
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  const validOURef = React.useRef(validOU);

  const getTreeComponent = (componentId: number, part?: IRackComponent): IRackComponent | undefined => {
    if (!componentTree) {
      return undefined;
    }
    const comp = part || componentTree;
    if (comp.componentId == componentId) {
      return comp;
    }
    for (let c of comp.components) {
      const sub = getTreeComponent(componentId, c);
      if (sub) {
        return sub;
      }
    }
    return undefined;
  }

  const findComponentByOU = (compTree: IRackComponent, ou: number, column: IComponentColumn): IRackComponent | undefined => {
    if (!compTree) {
      return undefined;
    }
    if (compTree.location && compTree.location.ou == ou && compTree.location.column == column) {
      return compTree;
    }
    for (let c of compTree.components) {
      const sub = findComponentByOU(c, ou, column);
      if (sub) {
        return sub;
      }
    }
    return undefined;
  }

  const prepareTree = (compTree: IRackComponent) => {
    sortTree(compTree);
    setComponentTree(compTree);
    onElevationChange();
    forceUpdate();
  }

  const getComponents = async (rackId: number) => {
    const accessToken = await getAccessTokenSilently();
    const resp = await API.get(accessToken, `/api/v1/rack/${rackId}/components`)
      .catch(err => {
        setDisplayState(ILoadState.ERROR);
      });
    const comps = makeSortedComponents(resp.data as IComponent[]);
    prepareTree(comps);
    setDisplayState(ILoadState.READY);
  }

  function onSaveSerial(componentId: number) {
    const comp = getTreeComponent(componentId)
    if (comp == undefined) {
      console.log('error finding tree element ' + componentId);
      return;
    }
    getAccessTokenSilently().then(accessToken => {
      API.post(accessToken, `/api/v1/component/${comp.componentId}/serial`, { serialNumber: comp.serialNumber })
        .then(res => {
          onElevationChange();
        })
        .catch(err => {
          Alert(AlertTypes.ERROR, err.message)
        });
    });
  }

  const onSerialChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSerial(e.currentTarget.value);
  }
  const onOUChange = (e: React.FormEvent<HTMLInputElement>) => {
    const isNumeric = /^\d*$/.test(e.currentTarget.value);
    if (isNumeric) {
      setOu(e.currentTarget.value);
      setColumn('');
    }
    setValidOU(isNumeric);
  }

  const onColumnChange = (e: React.FormEvent<HTMLInputElement>) => {
    if (!componentTree) {
      return;
    }
    const isNumeric = /^\d*$/.test(e.currentTarget.value);
    if (isNumeric) {
      setColumn(e.currentTarget.value);
    }
    setValidColumn(isNumeric);

    console.log(' looking for component at ' + ou + ':' + e.currentTarget.value);
    const comp = findComponentByOU(componentTree, +ou, +e.currentTarget.value);
    console.log('   found: ' + comp);
    if (comp) {
      setValidColumn(true);
      setValidOU(true);
      validOURef.current = true;
      return;
    } else {
      validOURef.current = false;
    }
  }

  const saveScannedSerial = () => {
    if (!setSaveSerial) {
      return;
    }
    setSaveSerial(false);
    if (!componentTree) {
      return;
    }
    if (validOU && validColumn && column != '' && ou != '' && serial != '') {
      const comp = findComponentByOU(componentTree, +ou, +column);
      if (comp) {
        getAccessTokenSilently().then(accessToken => {
        API.post(accessToken, `/api/v1/component/${comp.componentId}/serial`, { serialNumber: serial })
          .then(res => {
            comp.serialNumber = serial;
            setOu('');
            setColumn('');
            setSerial('');
            onElevationChange();
            if (comp.updateHook) {
              comp.updateHook();
            }
          })
          .catch(err => {
            Alert(AlertTypes.ERROR, err.message);
          });
        });
      } else {
        console.log('tried to save invalid serial')
      }
    }
  }

  function onSequentialBlur() {
    console.log('In sequetntialBlur');
    if (!validOURef.current) {
      const el = document.getElementById('ouField');
      if (el) {
        console.log('keeping focus');
        setTimeout(() => el.focus(), 50);
        beep(70, 'sine', 440, setTimeout(() => beep(70, 'sine', 380, undefined), 130));
      }
    } else {
      console.log(' in column field');
      const el = document.getElementById('serialField');
      if (el) {
        setSaveSerial(true);
        setTimeout(() => el.focus(), 50); // should probably trap the keyup instead... but
        beep(70, 'sine', 610, setTimeout(() => beep(70, 'sine', 610, undefined), 130));
      }
    }
  }

  const registerTabListener = () => {
    window.addEventListener('keydown', (e) => {
      console.log('in listener');
      if (e.keyCode == 9 && e.target) {
        console.log('::: tab & target');
        const el = e.srcElement as HTMLElement;
        console.log('::: el: ' + el);
        //        const id = el.id;
        if (el && el.id == 'columnField') {
          console.log('::: id: ' + el.id)
          console.log(':::: blurring');
          onSequentialBlur();
        }
      }
    })
  }

  useEffect(() => {
    getComponents(rackId);
    registerTabListener();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    saveScannedSerial();
  }, [saveSerial]);

  if (isLoading || displayState == ILoadState.NEW || displayState == ILoadState.LOADING || !componentTree) {
    return <div>
      <Spinner />
    </div>
  }
  if (displayState == ILoadState.ERROR) {
    return <div>
      <p>Error loading rack</p>
    </div>
  }

  return <form>
    <br />
    <h2>Sequential Scan</h2>
    <div className="uk-grid">
      <div>
        <label>Serial</label>
        <br />
        <input type="text"
          id="serialField"
          size={1}
          maxLength={40}
          onChange={e => onSerialChange(e)}
          value={serial}
          className="uk-input uk-form-small uk-form-width-large"
        />
      </div>
      <div>
        <label className={validOU ? '' : 'uk-text-danger'}>OU</label>
        <br />
        <input type="text"
          id="ouField"
          size={1}
          maxLength={4}
          value={ou}
          onChange={e => onOUChange(e)}
          className={"uk-input uk-form-small uk-form-width-extrasmall" + (validOU ? "" : " uk-form-danger")}
        />
      </div>
      <div>
        <label className={validColumn ? '' : 'uk-text-danger'}>Column</label>
        <br />
        <input type="text"
          id="columnField"
          size={1}
          maxLength={4}
          value={column}
          onChange={e => onColumnChange(e)}
          className={"uk-input uk-form-small uk-form-width-extrasmall" + (validColumn ? "" : " uk-form-danger")}
        />
      </div>
    </div>
    <h2>Location Scan</h2>
    <table className="uk-table uk-table-small">
      <thead>
        <tr>
          <th className="uk-text-nowrap">Part:Rev</th>
          <th>OU</th>
          <th className="uk-text-nowrap">Serial Number</th>
          <th>Name</th>
        </tr>
      </thead>
      <tbody>
        <DisplayRackSerialComponents part={componentTree} onSave={onSaveSerial} onElevationChange={onElevationChange} />
      </tbody>
    </table>
  </form >
}

interface DisplayRackSerialComponentsProps {
  part: IRackComponent;
  onElevationChange: () => void;
  onSave: (componentId: number) => void;
}
const DisplayRackSerialComponents = ({ part, onSave, onElevationChange }: DisplayRackSerialComponentsProps) => {

  const [serial, setSerial] = useState(part.serialNumber || '');
  const [expand, setExpand] = useState(part.level < 2);

  const onSerialChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSerial(e.currentTarget.value);
  }
  const onUpdate = (e: React.FocusEvent<HTMLInputElement>) => {
    if (part.serialNumber !== serial) {
      part.serialNumber = serial;
      onSave(part.componentId);
    }
  }

  // set this so other activities can tell us they changed the tree
  part.updateHook = () => {
    setSerial(part.serialNumber);
  }

  const location = part.location;
  return <>
    <tr>
      <td className="uk-preserve-width uk-text-nowrap">
        {[...Array(part.level! - 1)].map((e, i) => <span key={`${i}_${part.componentId}`} data-uk-icon="chevron-double-right" />)}
        {!expand && part.components.length > 0 ? <span data-uk-icon="triangle-right" onClick={(e) => setExpand(true)} /> : <></>}
        {expand && part.components.length > 0 ? <span data-uk-icon="triangle-down" onClick={(e) => setExpand(false)} /> : <></>}
        {part.components.length == 0 ? <span data-uk-icon="blank" /> : <></>}
        {part.partNumber}:{part.revision}
      </td>
      <td>
        {location ? location.ou : ''}
        {location && location.ou ? ':' : ''}
        {location ? location.column : ''}
      </td>
      <td className="uk-text-nowrap">
        {(part.level == 1 || (part.level == 2 && part.location && part.location.ou && part.location.column && part.level)) ? <>
          <input type="text"
            size={1}
            maxLength={40}
            onChange={e => onSerialChange(e)}
            onBlur={e => onUpdate(e)}
            value={serial}
            className="uk-input uk-form-small uk-form-width-small"
          />
        </> : <i>{part.level == 2 ? 'Location unassigned' : ''}</i>
        }
      </td>
      <td>
        {part.name}
      </td>
    </tr>
    {expand ? <>{part.components.map(p => <DisplayRackSerialComponents
      part={p}
      onSave={onSave}
      onElevationChange={onElevationChange}
      key={p.componentId} />)}</> : <></>}
  </>

}
